import { useState, useEffect, useRef } from 'react';
import { api, apiRoutes } from '../../api';
import '@fontsource/vazirmatn';
import './Chatbot.css';

const CHAT_STORAGE_KEY = 'saita_chat_messages';
const initialMessages = () => [{
    id: `welcome-${Date.now()}`,
    type: 'bot',
    message: 'سلام! من دستیار هوشمند شما هستم. چطور می‌توانم به شما کمک کنم؟',
}];

const restoreMessages = () => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(CHAT_STORAGE_KEY));
        return Array.isArray(saved) && saved.length > 0 ? saved : initialMessages();
    } catch {
        return initialMessages();
    }
};

const messageId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function Chatbot({ onResponse, onSessionReset }) {
    // Chatbot state management
    const [chatMessages, setChatMessages] = useState(restoreMessages);
    const [chatInput, setChatInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [, setLastActivity] = useState(Date.now());
    const [, setSessionKey] = useState(null);
    const [browserSessionId, setBrowserSessionId] = useState(null);
    const messagesEndRef = useRef(null);

    // Generate browser session ID on component mount
    useEffect(() => {
        let sessionId = sessionStorage.getItem('browser_session_id');
        if (!sessionId) {
            sessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
            sessionStorage.setItem('browser_session_id', sessionId);
        }
        setBrowserSessionId(sessionId);
    }, []);

    // Session is now managed automatically on the backend
    // No need to initialize session on component mount

    // A visual drawer may unmount this component. Persist conversation UI and
    // never close the semantic chat session just because a panel was hidden.
    useEffect(() => {
        sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chatMessages));
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [chatMessages, isLoading]);

    // Auto-close session after inactivity - DISABLED FOR TESTING
    // useEffect(() => {
    //     const checkInactivity = () => {
    //         const now = Date.now();
    //         const inactivityDuration = 30 * 60 * 1000;

    //         if (now - lastActivity > inactivityDuration) {
    //             handleCloseSession();
    //             setChatMessages(prev => [...prev, {
    //                 type: 'bot',
    //                 message: 'جلسه به دلیل عدم فعالیت بسته شد و خلاصه به‌روزرسانی شد. جلسه جدید آغاز شد. ⏰'
    //             }]);
    //         }
    //     };

    //     if (inactivityTimeoutRef.current) {
    //         clearTimeout(inactivityTimeoutRef.current);
    //     }

    //     inactivityTimeoutRef.current = setTimeout(checkInactivity, 5 * 60 * 1000);

    //     return () => {
    //         if (inactivityTimeoutRef.current) {
    //             clearTimeout(inactivityTimeoutRef.current);
    //         }
    //     };
    // }, [lastActivity]);

    const sendChatMessage = async () => {
        if (!chatInput.trim() || isLoading) return;

        const userMessage = chatInput.trim();
        setChatInput('');
        setIsLoading(true);
        setLastActivity(Date.now());

        setChatMessages(prev => [...prev, { id: messageId(), type: 'user', message: userMessage }]);

        try {
            const token = localStorage.getItem('access_token');

            if (!token) {
                throw new Error('No authentication token found. Please log in.');
            }

            const response = await api.post(apiRoutes.chat.message, {
                message: userMessage,
                browser_session_id: browserSessionId,
                match_page: 1,
                match_page_size: 50,
            }, {
                headers: { 
                    Authorization: `Bearer ${token}`
                }
            });

            setChatMessages(prev => [...prev, {
                id: messageId(),
                type: 'bot',
                message: response.data.message,
                matchCount: response.data.matches?.total,
                visibleMatchCount: response.data.matches?.results?.length,
            }]);
            onResponse?.(response.data);
        } catch (error) {

            let errorMessage = 'متاسفانه خطایی رخ داد. لطفا دوباره تلاش کنید.';
            if (error.response?.status === 401) {
                errorMessage = 'لطفا دوباره وارد حساب کاربری خود شوید.';
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            }

            setChatMessages(prev => [...prev, {
                id: messageId(),
                type: 'bot',
                message: errorMessage,
                retryText: userMessage,
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseSession = async () => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            alert('لطفا وارد حساب کاربری خود شوید');
            return;
        }

        try {
            // Close current session
            await api.post(apiRoutes.chat.sessionClose, {
                browser_session_id: browserSessionId
            }, {
                headers: { 
                    Authorization: `Bearer ${token}`
                }
            });

            // Initialize new session (this will create a new browser session ID)
            const newBrowserSessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
            sessionStorage.setItem('browser_session_id', newBrowserSessionId);
            setBrowserSessionId(newBrowserSessionId);

            const newSessionResponse = await api.post(apiRoutes.chat.sessionInit, {
                browser_session_id: newBrowserSessionId
            }, {
                headers: { 
                    Authorization: `Bearer ${token}`
                }
            });

            if (newSessionResponse.data.session_key) {
                setSessionKey(newSessionResponse.data.session_key);
            }
            onSessionReset?.();

            setChatMessages([{
                id: messageId(),
                type: 'bot',
                message: 'جلسه قبلی پایان یافت. یک گفت‌وگوی تازه شروع شده است. چطور می‌توانم کمک کنم؟'
            }]);
        } catch (error) {

            setChatMessages(prev => [...prev, {
                id: messageId(),
                type: 'bot',
                message: 'خطا در بستن جلسه. لطفا دوباره تلاش کنید.'
            }]);
        }
    };
    const handleChatInputKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendChatMessage();
        }
    };

    return (
        <div className="chatbot-container">
            <div className="chatbot-messages" role="log" aria-live="polite" aria-relevant="additions">
                {chatMessages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`chat-message ${msg.type}`}
                    >
                        {msg.message}
                        {msg.type === 'bot' && Number.isFinite(msg.matchCount) && (
                            <div className="chat-match-count">
                                {msg.visibleMatchCount < msg.matchCount
                                    ? `${msg.visibleMatchCount} نتیجه برتر از ${msg.matchCount} ملک منطبق نمایش داده شد`
                                    : `${msg.matchCount} ملک منطبق روی نقشه نمایش داده شد`}
                            </div>
                        )}
                        {msg.retryText && (
                            <button
                                type="button"
                                className="chat-retry-button"
                                onClick={() => setChatInput(msg.retryText)}
                            >
                                تلاش دوباره
                            </button>
                        )}
                    </div>
                ))}
                {isLoading && (
                    <div className="chat-message bot chat-typing" role="status">
                        <span /><span /><span />
                        <span className="sr-only">دستیار در حال پاسخ است</span>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <div className="chatbot-input-area">
                {/* Session Close Button */}
                <div className="session-close-button-container">
                    <button
                        onClick={handleCloseSession}
                        className="session-close-button"
                    >
                        پایان جلسه و به‌روزرسانی خلاصه
                    </button>
                </div>

                {/* Chat Input */}
                <div className="chat-input-container">
                    <textarea
                        rows="1"
                        placeholder="پیام خود را بنویسید..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={handleChatInputKeyDown}
                        disabled={isLoading}
                        className="chat-input"
                        aria-label="پیام به دستیار"
                    />
                    <button
                        onClick={sendChatMessage}
                        disabled={isLoading || !chatInput.trim()}
                        className={`chat-send-button ${isLoading || !chatInput.trim() ? 'disabled' : 'enabled'}`}
                    >
                        {isLoading ? 'در حال ارسال...' : 'ارسال'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Chatbot;
